import { useMutation } from "@apollo/client";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AttachFile from "@mui/icons-material/AttachFile";
import StopIcon from "@mui/icons-material/Stop";
import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { runConcurrently } from "encamp-shared/src/concurrency";
import { gql } from "generated-graphql";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import type { ScoutAttachment } from "../scoutSubmitAttachments";
import type { ScoutFilePart } from "./message/scoutChatMessage";

import { uploadFileDirectly } from "../../../../util/uploadFile";
import { SCOUT_MAX_ATTACHMENTS, validateScoutAttachmentFile } from "../scoutAttachmentLimits";
import { ScoutFileDisplay } from "./message/ScoutFileDisplay";

const INPUT_SCROLLBAR_WIDTH_PX = 12;
const INPUT_ACTION_OFFSET_PX = 20;
const INPUT_ACTION_RIGHT_PADDING_PX = 112;

const ATTACH_SCOUT_PRIVATE_DOCUMENT = gql(`
  mutation AttachScoutPrivateDocument($input: AttachScoutPrivateDocumentInput!) {
    attachScoutPrivateDocument(input: $input) {
      id
      title
      filename
      fileExtension
      mediaType
      sizeBytes
      ingestionMode
      extractionStatus
      signedDownloadUrl
    }
  }
`);

const DELETE_SCOUT_PRIVATE_DOCUMENT = gql(`
  mutation DeleteScoutPrivateDocument($input: DeleteScoutPrivateDocumentInput!) {
    deleteScoutPrivateDocument(input: $input) {
      deletedPrivateDocumentId
    }
  }
`);

type ScoutAttachmentError = {
  id: string;
  filename: string;
  mediaType: string;
  message: string;
};

export type { ScoutAttachment };

export type ScoutSubmitHandler = (
  message: string,
  attachments?: ScoutAttachment[]
) => Promise<void>;

export const ScoutInput = memo(function ScoutThreadInput({
  onSubmit,
  onAbort,
  isSending,
  tenantId,
  conversationId,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  isAborting = false,
  placeholder = "Chat with Scout…",
  autoFocus = false,
  lastUserMessage,
}: {
  onSubmit: ScoutSubmitHandler;
  onAbort?: () => void;
  isSending: boolean;
  tenantId?: string;
  conversationId?: string;
  onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
  onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
  isAborting?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Most recent user message text. ArrowUp on an empty input fills it in so
   * the user can quickly retry after an errored turn without retyping.
   */
  lastUserMessage?: string;
}) {
  const theme = useTheme();
  const [hasContent, setHasContent] = useState(false);
  const [attachments, setAttachments] = useState<ScoutAttachment[]>([]);
  const [attachmentErrors, setAttachmentErrors] = useState<ScoutAttachmentError[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<
    { completed: number; total: number } | undefined
  >();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachPrivateDocument] = useMutation(ATTACH_SCOUT_PRIVATE_DOCUMENT);
  const [deletePrivateDocument] = useMutation(DELETE_SCOUT_PRIVATE_DOCUMENT);
  const [removingAttachmentIds, setRemovingAttachmentIds] = useState<Set<string>>(() => new Set());
  const isInputLocked = isUploading || removingAttachmentIds.size > 0;
  const hasReachedAttachmentLimit = attachments.length >= SCOUT_MAX_ATTACHMENTS;
  const isAttachmentActionDisabled = isInputLocked || hasReachedAttachmentLimit;
  const isSubmitDisabled = !hasContent || isInputLocked || isSending;
  const inputSurfaceColor = theme.palette.background.paper;
  const inputScrollbarThumbColor = alpha(
    theme.palette.text.primary,
    theme.palette.mode === "light" ? 0.24 : 0.34
  );

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const clearInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setHasContent(false);
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setAttachmentErrors([]);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      focusInput();
    }
  }, [autoFocus, focusInput]);

  const uploadAttachments = useCallback(
    async (files: File[]) => {
      if (isInputLocked) {
        return;
      }

      const nextErrors: ScoutAttachmentError[] = [];
      const remainingAttachmentSlots = SCOUT_MAX_ATTACHMENTS - attachments.length;
      if (remainingAttachmentSlots <= 0) {
        setAttachmentErrors([
          {
            id: `attachment-limit-${Date.now()}`,
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: `You can attach up to ${SCOUT_MAX_ATTACHMENTS} files per message.`,
          },
        ]);
        return;
      }
      if (!tenantId) {
        setAttachmentErrors([
          {
            id: "missing-tenant",
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: "Scout requires an organization before attaching files.",
          },
        ]);
        return;
      }
      if (files.length === 0) {
        return;
      }

      const validFiles: File[] = [];
      for (const file of files) {
        const error = validateScoutAttachmentFile(file);
        if (error) {
          nextErrors.push({
            id: `${file.name}-${file.size}-${file.lastModified}-validation`,
            filename: file.name,
            mediaType: file.type || "application/octet-stream",
            message: error,
          });
        } else {
          validFiles.push(file);
        }
      }
      if (validFiles.length === 0) {
        setAttachmentErrors(nextErrors);
        return;
      }

      const filesToUpload = validFiles.slice(0, remainingAttachmentSlots);

      if (filesToUpload.length === 0) {
        setAttachmentErrors(nextErrors);
        return;
      }

      setAttachmentErrors([]);
      setIsUploading(true);
      setUploadProgress({ completed: 0, total: filesToUpload.length });

      try {
        const { results, failures, totalFailures } = await runConcurrently({
          items: filesToUpload,
          maxConcurrency: filesToUpload.length,
          allSettled: true,
          task: async (file) => {
            const uploadKey = await uploadFileDirectly(file, { tenantId });
            const attachResult = await attachPrivateDocument({
              variables: {
                input: {
                  tenantId,
                  conversationId: conversationId ?? null,
                  uploadKey,
                  filename: file.name,
                  mediaType: file.type || "application/octet-stream",
                  sizeBytes: file.size,
                },
              },
            });
            const attached = attachResult.data?.attachScoutPrivateDocument;
            if (!attached) {
              throw new Error("Scout could not register this attachment.");
            }

            setUploadProgress((previous) =>
              previous
                ? {
                    completed: previous.completed + 1,
                    total: previous.total,
                  }
                : undefined
            );

            return {
              privateDocumentId: attached.id,
              filename: file.name,
              title: file.name,
              mediaType: attached.mediaType,
              sizeBytes: attached.sizeBytes,
              signedDownloadUrl: attached.signedDownloadUrl ?? undefined,
              ingestionMode: attached.ingestionMode,
              extractionStatus: attached.extractionStatus,
            } satisfies ScoutAttachment;
          },
        });

        if (totalFailures > 0) {
          nextErrors.push(
            ...failures.map(({ item, error }) => ({
              id: `${item.name}-${item.size}-${item.lastModified}-upload`,
              filename: item.name,
              mediaType: item.type || "application/octet-stream",
              message: error.message || "Scout could not upload this file.",
            }))
          );
        }

        if (results.length > 0) {
          setAttachments((previous) => [...previous, ...results]);
          onAttachmentsUploaded?.(results);
        }
        if (nextErrors.length > 0) {
          setAttachmentErrors(nextErrors);
        }
      } catch (error) {
        setAttachmentErrors([
          ...nextErrors,
          {
            id: `upload-failed-${Date.now()}`,
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: error instanceof Error ? error.message : "Scout could not upload files.",
          },
        ]);
      } finally {
        setIsUploading(false);
        setUploadProgress(undefined);
      }
    },
    [
      attachments.length,
      attachPrivateDocument,
      conversationId,
      isInputLocked,
      onAttachmentsUploaded,
      tenantId,
    ]
  );

  const handleSubmit = useCallback(async () => {
    const value = inputRef.current?.value ?? "";
    if (!value.trim() || isSending || isInputLocked) {
      return;
    }

    const attachmentPayload = attachments.length > 0 ? [...attachments] : undefined;
    clearInput();
    clearAttachments();
    focusInput();
    try {
      await onSubmit(value.trim(), attachmentPayload);
    } finally {
      focusInput();
    }
  }, [attachments, clearAttachments, clearInput, focusInput, isInputLocked, isSending, onSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHasContent = !!e.target.value.trim();
    setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
  }, []);

  const handleRemoveAttachment = useCallback(
    async (privateDocumentId: string) => {
      if (isInputLocked || !tenantId) {
        return;
      }

      const removedAttachment = attachments.find(
        (attachment) => attachment.privateDocumentId === privateDocumentId
      );
      if (!removedAttachment) {
        return;
      }

      setAttachments((previous) =>
        previous.filter((attachment) => attachment.privateDocumentId !== privateDocumentId)
      );
      setRemovingAttachmentIds((previous) => new Set(previous).add(privateDocumentId));

      try {
        await deletePrivateDocument({
          variables: {
            input: {
              tenantId,
              privateDocumentId,
              conversationId: conversationId ?? null,
            },
          },
        });
        await onAttachmentRemoved?.(privateDocumentId);
      } catch (error) {
        setAttachments((previous) => [...previous, removedAttachment]);
        setAttachmentErrors((previous) => [
          ...previous,
          {
            id: `${privateDocumentId}-delete`,
            filename: removedAttachment.filename,
            mediaType: removedAttachment.mediaType,
            message:
              error instanceof Error ? error.message : "Scout could not remove this attachment.",
          },
        ]);
      } finally {
        setRemovingAttachmentIds((previous) => {
          const next = new Set(previous);
          next.delete(privateDocumentId);
          return next;
        });
      }
    },
    [
      attachments,
      conversationId,
      deletePrivateDocument,
      isInputLocked,
      onAttachmentRemoved,
      tenantId,
    ]
  );

  const handleRemoveAttachmentError = useCallback(
    (errorId: string) => {
      if (isInputLocked) {
        return;
      }

      setAttachmentErrors((previous) => previous.filter((error) => error.id !== errorId));
    },
    [isInputLocked]
  );

  const handleFilePickerChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isInputLocked) {
        event.currentTarget.value = "";
        return;
      }

      const files = event.target.files ? [...event.target.files] : [];
      event.currentTarget.value = "";
      await uploadAttachments(files);
    },
    [isInputLocked, uploadAttachments]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (isInputLocked || isSending) {
        return;
      }
      await uploadAttachments([...event.dataTransfer.files]);
    },
    [isInputLocked, isSending, uploadAttachments]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isInputLocked) {
        return;
      }

      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        void handleSubmit();
      } else if (event.key === "ArrowUp") {
        const textarea = inputRef.current;
        // Only repopulate when the field is empty so we don't clobber what
        // the user is typing or interfere with normal arrow-key navigation
        // inside a multi-line draft.
        if (textarea && textarea.value === "" && lastUserMessage) {
          event.preventDefault();
          textarea.value = lastUserMessage;
          textarea.dispatchEvent(new Event("input", { bubbles: true }));
          setHasContent(true);
          // Place caret at end so the next keystroke appends.
          const end = lastUserMessage.length;
          textarea.setSelectionRange(end, end);
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        if (isSending && onAbort && !isAborting) {
          onAbort();
          return;
        }
        const textarea = inputRef.current;
        if (textarea && textarea.value.trim()) {
          clearInput();
          textarea.focus();
        } else if (textarea) {
          textarea.blur();
        }
      }
    },
    [clearInput, handleSubmit, isAborting, isInputLocked, isSending, lastUserMessage, onAbort]
  );

  return (
    <Box
      onDragOver={(event) => {
        if (isInputLocked) {
          return;
        }
        event.preventDefault();
      }}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 920,
        mx: "auto",
        px: 2,
        py: 1.25,
        borderRadius: (theme) => theme.spacing(3),
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ maxHeight: 200, overflow: "auto" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/gif,image/webp,text/plain"
          multiple
          disabled={isAttachmentActionDisabled}
          onChange={handleFilePickerChange}
          style={{ display: "none" }}
        />
        {attachments.length > 0 && (
          <Stack spacing={1} sx={{ mb: 1 }}>
            {attachments.map((attachment) => {
              const part: ScoutFilePart = {
                id: attachment.privateDocumentId,
                type: "file",
                title: attachment.title,
                filename: attachment.filename,
                mediaType: attachment.mediaType,
                url: `encamp-private-document://${attachment.privateDocumentId}`,
                ingestionMode: attachment.ingestionMode,
                extractionStatus: attachment.extractionStatus,
                document: attachment.signedDownloadUrl
                  ? {
                      id: attachment.privateDocumentId,
                      signedDownloadUrl: attachment.signedDownloadUrl,
                    }
                  : undefined,
              };
              return (
                <Stack key={attachment.privateDocumentId} direction="row">
                  <Box sx={{ flex: 0.5 }}>
                    <ScoutFileDisplay
                      part={part}
                      onRemove={
                        isInputLocked
                          ? undefined
                          : () => handleRemoveAttachment(attachment.privateDocumentId)
                      }
                    />
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        )}
        {attachmentErrors.length > 0 && (
          <Stack spacing={1} sx={{ mb: 1 }}>
            {attachmentErrors.map((attachmentError) => {
              const errorPart: ScoutFilePart = {
                id: attachmentError.id,
                type: "file",
                title: attachmentError.filename,
                filename: attachmentError.filename,
                mediaType: attachmentError.mediaType,
              };
              return (
                <Stack key={attachmentError.id} direction="row">
                  <Box sx={{ flex: 0.5 }}>
                    <ScoutFileDisplay
                      part={errorPart}
                      state="error"
                      errorText={attachmentError.message}
                      onRemove={
                        isInputLocked
                          ? undefined
                          : () => handleRemoveAttachmentError(attachmentError.id)
                      }
                    />
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        )}
        {uploadProgress && uploadProgress.completed !== uploadProgress.total && (
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Uploading {uploadProgress.completed + 1} of {uploadProgress.total}…
            </Typography>
            <LinearProgress />
          </Stack>
        )}
      </Box>

      <TextField
        multiline
        minRows={1}
        maxRows={8}
        fullWidth
        variant="standard"
        slotProps={{
          input: { disableUnderline: true },
          htmlInput: { "aria-label": "Message Scout" },
        }}
        onChange={handleChange}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        sx={{
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
          },
          "& .MuiInputBase-inputMultiline": {
            padding: "4px 0",
            paddingRight: `${INPUT_ACTION_RIGHT_PADDING_PX}px`,
            minHeight: 24,
            lineHeight: 1.5,
            overflowX: "hidden",
            overflowY: "auto",
            scrollbarGutter: "stable",
            scrollbarWidth: "thin",
            scrollbarColor: `${inputScrollbarThumbColor} ${inputSurfaceColor}`,
            "&::-webkit-scrollbar": {
              width: `${INPUT_SCROLLBAR_WIDTH_PX}px`,
              backgroundColor: inputSurfaceColor,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: inputSurfaceColor,
              border: "none",
              boxShadow: "none",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: inputScrollbarThumbColor,
              border: "none",
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: inputSurfaceColor,
            },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: `${INPUT_ACTION_OFFSET_PX}px`,
          bottom: 10,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {isSending && onAbort ? (
          <Tooltip title={isAborting ? "Stopping..." : "Stop generating (Esc)"} arrow>
            <span
              style={{
                display: "inline-flex",
                cursor: isAborting ? "default" : "pointer",
              }}
            >
              <IconButton
                onClick={onAbort}
                aria-label="Stop generating"
                color="primary"
                disabled={isAborting}
              >
                <StopIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <>
            <Tooltip
              title={
                hasReachedAttachmentLimit
                  ? `Attachment limit reached (${SCOUT_MAX_ATTACHMENTS})`
                  : `Attach up to ${SCOUT_MAX_ATTACHMENTS} files`
              }
              arrow
            >
              <span
                style={{
                  display: "inline-flex",
                  cursor: isInputLocked ? "default" : "pointer",
                }}
              >
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={isAttachmentActionDisabled}
                  aria-label={`Attach up to ${SCOUT_MAX_ATTACHMENTS} files`}
                  color="primary"
                >
                  <AttachFile sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Send (Enter · Shift+Enter for new line)" arrow>
              <span
                style={{
                  display: "inline-flex",
                  cursor: isSubmitDisabled ? "default" : "pointer",
                }}
              >
                <IconButton
                  onClick={() => {
                    void handleSubmit();
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={isSubmitDisabled}
                  aria-label="Send message"
                  color="primary"
                >
                  <ArrowUpwardIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
});
ScoutInput.displayName = "ScoutThreadInput";
